import React from 'react';

import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';

/*
 * The small panel primitives every 3D studio dock is built from. They lived in
 * Furniture3DStudioWidget; the take panels need them too, and importing them
 * from there would have made the widget and its own panels import each other.
 */

/** Right-panel section header — uppercase label + optional value on the right. */
export const StudioSectionHeader: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <div className="mb-2 flex items-baseline justify-between">
        <span className="text-xs font-medium tracking-[0.04em] text-muted-foreground uppercase">{label}</span>
        {value && <span className="text-sm font-medium text-foreground">{value}</span>}
    </div>
);

/**
 * Stepped slider with labelled notches. Marks show the presets; dragging
 * "detents" (magnetically snaps) onto a mark when close, but any value between
 * is still allowed. Pass `discrete` for options that only make sense AT a mark
 * (aspect ratio) — then it snaps to marks only.
 */
export const StudioMarkSlider: React.FC<{
    value: number;
    min: number;
    max: number;
    marks: Array<{ value: number; label?: string }>;
    onChange: (v: number) => void;
    discrete?: boolean;
}> = ({ value, min, max, marks, onChange, discrete }) => {
    const threshold = (max - min) * 0.028;
    const hasLabels = marks.some((m) => m.label != null);
    const handle = (v: number) => {
        let best = v;
        let bestD = Infinity;
        for (const m of marks) { const d = Math.abs(m.value - v); if (d < bestD) { bestD = d; best = m.value; } }
        // MUI's step=null emitted only mark values for discrete sliders; Radix
        // has no marks-only step, so snap every change to the nearest mark.
        if (discrete) { onChange(best); return; }
        onChange(bestD <= threshold ? best : v);
    };
    const pct = (v: number) => ((v - min) / (max - min)) * 100;
    return (
        // dir=ltr: the mark maths and the 3D values these sliders drive are an
        // LTR coordinate space — do not mirror in RTL.
        <div dir="ltr" className={cn('mt-1 px-[7px]', hasLabels ? 'mb-5' : 'mb-0')}>
            {/* onValueChange, not onValueCommit — these sliders drive the live
                3D lens/aperture and must update per-frame while dragging. */}
            <Slider
                value={[value]}
                min={min}
                max={max}
                step={1}
                onValueChange={(vals: number[]) => handle(vals[0])}
            />
            <div className="pointer-events-none relative mt-1.5 h-1">
                {marks.map((m) => (
                    <span
                        key={m.value}
                        className={cn(
                            'absolute top-0 size-[3px] -translate-x-1/2 rounded-full',
                            m.value <= value ? 'bg-foreground/30' : 'bg-foreground/20',
                        )}
                        style={{ left: `${pct(m.value)}%` }}
                    />
                ))}
            </div>
            {hasLabels && (
                <div className="pointer-events-none relative h-4">
                    {marks.map((m) => (m.label != null ? (
                        <span
                            key={m.value}
                            className={cn(
                                'absolute top-0 -translate-x-1/2 text-xs font-medium whitespace-nowrap',
                                m.value <= value ? 'text-foreground' : 'text-muted-foreground',
                            )}
                            style={{ left: `${pct(m.value)}%` }}
                        >
                            {m.label}
                        </span>
                    ) : null))}
                </div>
            )}
        </div>
    );
};

/** Preset button — bigger text, borderless; selected = a soft grey fill. */
export const StudioPill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            'cursor-pointer rounded-md border-0 px-1 py-1.5 text-center text-sm font-medium whitespace-nowrap transition-colors',
            active
                ? 'bg-foreground/15 text-foreground hover:bg-foreground/20'
                : 'bg-foreground/5 text-muted-foreground hover:bg-foreground/10 hover:text-foreground',
        )}
    >
        {children}
    </button>
);
